/**
 * スクロール位置に応じてサイドナビゲーションのアクティブ状態を更新する
 *
 * 処理の流れ:
 * 1. サイドナビの全リンクを取得し、対応するセクション要素とセットにする
 * 2. スクロールのたびに「現在どのセクションが見えているか」を判定する
 * 3. 対応するナビリンクに is-active クラスを付与し、それ以外から外す
 * 4. ナビリンクをクリックした場合も即座にアクティブ状態を更新する
 */
export function initScrollActiveNav() {
  'use strict';

  // href="#..." 形式のサイドナビリンクをすべて取得する
  const navItems = Array.from(document.querySelectorAll('.js-sideNav__item[href^="#"]'));

  // ナビリンクが1つもなければ処理を中断する
  if (!navItems.length) {
    return;
  }

  // 各ナビリンクについて、リンク先のセクション要素を取得して配列にまとめる
  // { id: "hero", section: <HTMLElement> } のような形のオブジェクトの配列になる
  const sectionMap = navItems
    .map((item) => {
      const href = item.getAttribute('href');

      // '#' で始まらない href は対象外とする
      if (!href || href.charAt(0) !== '#') {
        return null;
      }

      // href="#hero" ならば id="hero" の要素を探す
      const section = document.querySelector(href);

      // 対応するセクション要素が見つからなければスキップする
      if (!section) {
        return null;
      }

      return {
        id: href.slice(1),  // "#hero" → "hero"
        section: section
      };
    })
    .filter(Boolean); // null を除外する

  // 有効なセクションが1つもなければ処理を中断する
  if (!sectionMap.length) {
    return;
  }

  // -------------------------------------------------------
  // 指定した ID のナビリンクをアクティブ状態にする関数
  // -------------------------------------------------------
  const setActive = (activeId) => {
    navItems.forEach((item) => {
      const href = item.getAttribute('href') || '';
      const isActive = href === '#' + activeId;

      // is-active クラスをトグルする（true なら付与、false なら除去）
      item.classList.toggle('is-active', isActive);

      // アクセシビリティ: 現在のページ位置に対応するリンクを aria-current でスクリーンリーダーに伝える
      if (isActive) {
        item.setAttribute('aria-current', 'true');
      } else {
        item.removeAttribute('aria-current');
      }
    });
  };

  // -------------------------------------------------------
  // 現在のスクロール位置から「アクティブなセクションのID」を判定する関数
  // -------------------------------------------------------
  const detectActiveId = () => {
    // 画面の上から35%の位置を「判定ライン」とする
    // この位置を超えて上に入ったセクションを「現在のセクション」とみなす
    const marker = window.innerHeight * 0.35;

    // 初期値は最初のセクション（ページ最上部にいる場合のフォールバック）
    let candidateId = sectionMap[0].id;

    for (let i = 0; i < sectionMap.length; i += 1) {
      const entry = sectionMap[i];
      // getBoundingClientRect().top はそのセクションの上端が画面上のどこにいるかを返す
      const rect = entry.section.getBoundingClientRect();

      // セクションの上端が判定ライン以上に上にある場合、このセクションを候補とする
      // ループを続けることで、スクロールが進むほど後のセクションに上書きされていく
      if (rect.top <= marker) {
        candidateId = entry.id;
      }
    }

    // 最後のセクションも明示的にチェックする（ループ内で上書きされない場合のため）
    const lastEntry = sectionMap[sectionMap.length - 1];
    const lastRect = lastEntry.section.getBoundingClientRect();
    if (lastRect.top <= marker) {
      candidateId = lastEntry.id;
    }

    return candidateId;
  };

  // -------------------------------------------------------
  // スクロール・リサイズ時の更新処理（パフォーマンス最適化）
  // requestAnimationFrame で1フレームに1回だけ処理を実行する
  // -------------------------------------------------------
  let isTicking = false;

  const requestUpdate = () => {
    // すでにフレームのリクエスト済みなら何もしない（二重実行を防ぐ）
    if (isTicking) {
      return;
    }

    isTicking = true;
    window.requestAnimationFrame(() => {
      setActive(detectActiveId());
      isTicking = false; // 実行後にフラグをリセットして次回のリクエストを受け付ける
    });
  };

  // スクロール・リサイズ時にアクティブ状態を更新する
  window.addEventListener('scroll', requestUpdate, { passive: true }); // passive: true でスクロールのパフォーマンスを向上させる
  window.addEventListener('resize', requestUpdate);

  // ナビリンクをクリックした場合も即座にアクティブ状態を更新する
  // （スクロールが完了する前にアクティブ表示を先行させるため）
  navItems.forEach((item) => {
    item.addEventListener('click', () => {
      const href = item.getAttribute('href');
      if (href && href.charAt(0) === '#') {
        setActive(href.slice(1));
      }
    });
  });

  // ページ読み込み直後に一度実行して初期状態を反映する
  setActive(detectActiveId());
}